import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL = '#0d9488'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const STEPS = ['Personal & Insurance', 'Medical History', 'Pain & Injury', 'Lifestyle & Goals', 'Review & Consent']

const COMORBIDITIES = [
  'Diabetes', 'Hypertension', 'Heart Disease / Angina', 'Osteoporosis',
  'Arthritis / Joint Disease', 'Obesity', 'Depression / Anxiety',
  'Cancer (current or recent)', 'COPD / Asthma', 'Neuropathy / Nerve Damage',
  'Kidney Disease', 'Stroke / TIA', 'Blood Clotting Disorder', 'Pacemaker / Implant',
]

const BODY_REGION_GROUPS = [
  { label: 'Head & Spine',  cols: 2, regions: ['Head / Neck', 'Upper Back', 'Mid Back', 'Lower Back'] },
  { label: 'Left Side',     cols: 2, regions: ['Left Shoulder', 'Left Elbow', 'Left Wrist / Hand', 'Left Hip', 'Left Knee', 'Left Ankle / Foot'] },
  { label: 'Right Side',    cols: 2, regions: ['Right Shoulder', 'Right Elbow', 'Right Wrist / Hand', 'Right Hip', 'Right Knee', 'Right Ankle / Foot'] },
  { label: 'Torso',         cols: 2, regions: ['Chest / Ribs', 'Abdomen / Groin'] },
]

const PAIN_TYPES = [
  'Sharp', 'Dull / Aching', 'Burning', 'Throbbing',
  'Stabbing', 'Numbness / Tingling', 'Stiffness', 'Weakness',
]

const WORK_STATUS_OPTIONS = [
  'Full-time',
  'Part-time',
  'Modified duty / restrictions',
  'On medical leave',
  'On disability',
  'Retired',
  'Student',
  'Unemployed',
]

const emptyForm = () => ({
  // Personal
  dob: '', phone: '', address: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  referring_physician: '',
  // Insurance
  insurance_provider: '', insurance_policy_number: '',
  insurance_group_number: '', policy_holder_name: '',
  workers_comp: false, mva_related: false,
  // Medical History
  primary_care_physician: '', medications: '', allergies: '',
  prior_surgeries: '', prior_pt: false, prior_pt_notes: '',
  comorbidities: [], work_status: '',
  // Pain & Injury
  pain_current: 5, pain_worst: 7, pain_best: 2,
  pain_type: '', pain_body_regions: [],
  mechanism_of_injury: '', symptom_onset: '', pain_description: '',
  aggravating_factors: '', relieving_factors: '', functional_limitations: '',
  // Lifestyle
  occupation: '', sport_activities: '', activity_level: '', patient_goals: '',
  // Consent
  consent_initials: '', consent_signed: false,
})

export default function IntakeForm({ token }) {
  const [step, setStep] = useState(0)
  const [form, setForm] = useState(emptyForm())
  const [patientInfo, setPatientInfo] = useState(null)
  const [status, setStatus] = useState('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [accountForm, setAccountForm] = useState({ username: '', password: '', confirmPassword: '' })
  const [accountError, setAccountError] = useState('')
  const [patientId, setPatientId] = useState(null)
  const [clinicId, setClinicId] = useState(null)
  const DRAFT_KEY = `intake_draft_${token}`

  useEffect(() => {
    axios.get(`${API}/intake/${token}`)
      .then(res => {
        setPatientInfo(res.data)
        setPatientId(res.data.patient_id)
        setClinicId(res.data.clinic_id)
        // Restore saved draft
        try {
          const saved = localStorage.getItem(DRAFT_KEY)
          if (saved) {
            const { form: savedForm, step: savedStep } = JSON.parse(saved)
            if (savedForm) { setForm(savedForm); setStep(savedStep || 0) }
          }
        } catch (e) {}
        setStatus('ready')
      })
      .catch(err => {
        setErrorMsg(err.response?.data?.detail || 'This link is invalid or has expired.')
        setStatus('error')
      })
  }, [token])

  // Auto-save draft
  useEffect(() => {
    if (status !== 'ready') return
    const t = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step }))
    }, 600)
    return () => clearTimeout(t)
  }, [form, step, status])

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const toggleArrayItem = (field, item) => setForm(f => {
    const arr = f[field] || []
    return { ...f, [field]: arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item] }
  })

  const handleSubmit = () => {
    if (!form.consent_signed || !form.consent_initials.trim()) {
      setErrorMsg('Please complete the consent section — type your initials and check the agreement box.')
      return
    }
    setStatus('submitting')
    setErrorMsg('')
    const payload = {
      ...form,
      comorbidities: JSON.stringify(form.comorbidities),
      pain_body_regions: JSON.stringify(form.pain_body_regions),
    }
    axios.post(`${API}/intake/${token}`, payload)
      .then(res => {
        setPatientId(res.data.patient_id)
        setClinicId(res.data.clinic_id)
        localStorage.removeItem(DRAFT_KEY)
        setStatus('create_account')
      })
      .catch(() => {
        setErrorMsg('Something went wrong. Please try again.')
        setStatus('ready')
      })
  }

  const handleCreateAccount = () => {
    setAccountError('')
    if (!accountForm.username || !accountForm.password) { setAccountError('Please fill in all fields.'); return }
    if (accountForm.password !== accountForm.confirmPassword) { setAccountError('Passwords do not match.'); return }
    if (accountForm.password.length < 6) { setAccountError('Password must be at least 6 characters.'); return }
    setStatus('creating')
    axios.post(`${API}/auth/create-user`, {
      username: accountForm.username, password: accountForm.password,
      patient_id: patientId, role: 'patient', clinic_id: clinicId
    })
      .then(() => setStatus('done'))
      .catch(err => { setAccountError(err.response?.data?.detail || 'Failed to create account.'); setStatus('create_account') })
  }

  //  Status screens 

  if (status === 'loading') return (
    <Screen><p style={{ color: '#64748b', textAlign: 'center', fontSize: '16px' }}>Loading your form...</p></Screen>
  )

  if (status === 'error') return (
    <Screen>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}></div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>Link unavailable</h2>
        <p style={{ color: '#64748b', fontSize: '16px' }}>{errorMsg}</p>
      </div>
    </Screen>
  )

  if (status === 'create_account' || status === 'creating') return (
    <Screen>
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}></div>
        <div style={{ fontSize: '22px', fontWeight: '800', color: TEAL, marginBottom: '4px', letterSpacing: '-0.03em' }}>ClearPath PT</div>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '0 0 6px' }}>Intake submitted!</h1>
        <p style={{ fontSize: '15px', color: '#64748b', margin: 0 }}>Create a login to access your patient portal and track your recovery.</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
        <Field label="Choose a username">
          <input placeholder="e.g. sarahjohnson" value={accountForm.username} onChange={e => setAccountForm(f => ({ ...f, username: e.target.value }))} style={iS} autoCapitalize="none" autoCorrect="off" />
        </Field>
        <Field label="Create a password" hint="At least 6 characters">
          <input type="password" placeholder="Password" value={accountForm.password} onChange={e => setAccountForm(f => ({ ...f, password: e.target.value }))} style={iS} />
        </Field>
        <Field label="Confirm password">
          <input type="password" placeholder="Re-enter password" value={accountForm.confirmPassword} onChange={e => setAccountForm(f => ({ ...f, confirmPassword: e.target.value }))} style={iS} />
        </Field>
        {accountError && <ErrBox>{accountError}</ErrBox>}
        <button onClick={handleCreateAccount} disabled={status === 'creating'} style={{ ...btnPrimary, opacity: status === 'creating' ? 0.7 : 1 }}>
          {status === 'creating' ? 'Creating account...' : 'Create Account →'}
        </button>
        <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', margin: 0 }}>Already have a login? <a href="/" style={{ color: TEAL, textDecoration: 'none', fontWeight: '500' }}>Sign in here</a></p>
      </div>
    </Screen>
  )

  if (status === 'done') return (
    <Screen>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '64px', height: '64px', background: '#f0fdfa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="16" fill={TEAL} />
            <polyline points="9,16 14,21 23,11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
          You're all set, {patientInfo?.patient_name?.split(' ')[0]}!
        </h2>
        <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '28px' }}>
          Your intake form has been submitted and your account is ready. Log in anytime to track your recovery progress.
        </p>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: TEAL, color: 'white', textDecoration: 'none', padding: '13px 28px', borderRadius: '8px', fontWeight: '600', fontSize: '16px', fontFamily: FONT }}>
          Go to Patient Portal →
        </a>
      </div>
    </Screen>
  )

  //  Main form 

  return (
    <Screen>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '22px', fontWeight: '800', color: TEAL, marginBottom: '4px', letterSpacing: '-0.03em' }}>ClearPath PT</div>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px' }}>Patient Intake Form</h1>
        <p style={{ fontSize: '15px', color: '#94a3b8', margin: 0 }}>
          Hi {patientInfo?.patient_name?.split(' ')[0]} — please answer as completely as you can
        </p>
        {patientInfo?.injury_type && (
          <p style={{ fontSize: '13px', color: TEAL, margin: '6px 0 0', fontWeight: '600' }}>{patientInfo.injury_type}</p>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{
              fontSize: '10px', fontWeight: i === step ? '700' : '400',
              color: i === step ? TEAL : i < step ? '#0f172a' : '#cbd5e0',
              textAlign: 'center', flex: 1, lineHeight: '1.3',
            }}>
              {i < step ? '' : i + 1}
            </div>
          ))}
        </div>
        <div style={{ height: '5px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${((step + 1) / STEPS.length) * 100}%`, background: TEAL, borderRadius: '99px', transition: 'width 0.3s ease' }} />
        </div>
        <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>
          Step {step + 1} of {STEPS.length}: <strong style={{ color: '#475569' }}>{STEPS[step]}</strong>
        </div>
      </div>

      {errorMsg && <ErrBox style={{ marginBottom: '16px' }}>{errorMsg}</ErrBox>}

      {/* Step content */}
      <div style={{ marginBottom: '24px' }}>
        {step === 0 && <Step0 form={form} set={set} />}
        {step === 1 && <Step1 form={form} set={set} toggleArrayItem={toggleArrayItem} />}
        {step === 2 && <Step2 form={form} set={set} toggleArrayItem={toggleArrayItem} />}
        {step === 3 && <Step3 form={form} set={set} />}
        {step === 4 && <Step4 form={form} set={set} patientName={patientInfo?.patient_name} />}
      </div>

      {/* Nav buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => { setStep(s => s - 1); setErrorMsg('') }}
          disabled={step === 0}
          style={{ padding: '11px 22px', border: '1.5px solid #e2e8f0', borderRadius: '8px', background: 'white', color: step === 0 ? '#cbd5e0' : '#334155', cursor: step === 0 ? 'default' : 'pointer', fontSize: '15px', fontWeight: '500', fontFamily: FONT }}
        >
          ← Back
        </button>
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={() => { setStep(s => s + 1); setErrorMsg('') }} style={btnPrimary}>
            Continue →
          </button>
        ) : (
          <button type="button" onClick={handleSubmit} disabled={status === 'submitting'} style={{ ...btnPrimary, opacity: status === 'submitting' ? 0.7 : 1 }}>
            {status === 'submitting' ? 'Submitting...' : 'Submit Form '}
          </button>
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '16px', marginBottom: 0 }}>
         Your progress is saved automatically — you can close this page and return using the same link.
      </p>
    </Screen>
  )
}

//  Step 0: Personal & Insurance 

function Step0({ form, set }) {
  return (
    <StepCard title="Personal & Insurance Information">
      <SectionLabel>Personal Details</SectionLabel>
      <TwoCol>
        <Field label="Date of Birth">
          <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)} style={iS} />
        </Field>
        <Field label="Phone Number">
          <input placeholder="(555) 555-5555" value={form.phone} onChange={e => set('phone', e.target.value)} style={iS} />
        </Field>
      </TwoCol>
      <Field label="Home Address">
        <input placeholder="123 Main St, City, State ZIP" value={form.address} onChange={e => set('address', e.target.value)} style={iS} />
      </Field>
      <TwoCol>
        <Field label="Emergency Contact Name">
          <input placeholder="Full name" value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} style={iS} />
        </Field>
        <Field label="Emergency Contact Phone">
          <input placeholder="(555) 555-5555" value={form.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} style={iS} />
        </Field>
      </TwoCol>
      <Field label="Referring Physician" hint="The doctor or specialist who referred you to physical therapy (if applicable)">
        <input placeholder="Dr. Name, Specialty" value={form.referring_physician} onChange={e => set('referring_physician', e.target.value)} style={iS} />
      </Field>

      <SectionLabel>Insurance / Billing</SectionLabel>
      <TwoCol>
        <Field label="Insurance Provider">
          <input placeholder="e.g. Blue Cross, Aetna, Medicare" value={form.insurance_provider} onChange={e => set('insurance_provider', e.target.value)} style={iS} />
        </Field>
        <Field label="Member / Policy Number">
          <input placeholder="e.g. XYZ123456789" value={form.insurance_policy_number} onChange={e => set('insurance_policy_number', e.target.value)} style={iS} />
        </Field>
      </TwoCol>
      <TwoCol>
        <Field label="Group Number">
          <input placeholder="e.g. G-456789" value={form.insurance_group_number} onChange={e => set('insurance_group_number', e.target.value)} style={iS} />
        </Field>
        <Field label="Policy Holder Name" hint="If different from you">
          <input placeholder="Full name" value={form.policy_holder_name} onChange={e => set('policy_holder_name', e.target.value)} style={iS} />
        </Field>
      </TwoCol>
      <Field label="Special billing circumstances">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
          <CheckboxItem
            label="This is a Workers' Compensation claim"
            checked={form.workers_comp}
            onChange={v => set('workers_comp', v)}
          />
          <CheckboxItem
            label="This is related to a motor vehicle accident (auto insurance)"
            checked={form.mva_related}
            onChange={v => set('mva_related', v)}
          />
        </div>
      </Field>
    </StepCard>
  )
}

//  Step 1: Medical History 

function Step1({ form, set, toggleArrayItem }) {
  return (
    <StepCard title="Medical History">
      <Field label="Primary Care Physician">
        <input placeholder="Dr. Name" value={form.primary_care_physician} onChange={e => set('primary_care_physician', e.target.value)} style={iS} />
      </Field>
      <Field label="Current Medications" hint="List all medications including over-the-counter and supplements">
        <textarea
          placeholder="e.g. Ibuprofen 400mg, Lisinopril 10mg — or 'None'"
          value={form.medications}
          onChange={e => set('medications', e.target.value)}
          style={{ ...iS, height: '80px', resize: 'vertical' }}
        />
      </Field>
      <TwoCol>
        <Field label="Allergies (medications, latex, etc.)">
          <textarea placeholder="e.g. Penicillin, latex — or 'None'" value={form.allergies} onChange={e => set('allergies', e.target.value)} style={{ ...iS, height: '72px', resize: 'vertical' }} />
        </Field>
        <Field label="Prior Surgeries or Hospitalizations">
          <textarea placeholder="e.g. Knee surgery 2019 — or 'None'" value={form.prior_surgeries} onChange={e => set('prior_surgeries', e.target.value)} style={{ ...iS, height: '72px', resize: 'vertical' }} />
        </Field>
      </TwoCol>
      <Field label="Have you had physical therapy before?">
        <div style={{ display: 'flex', gap: '20px', marginTop: '4px' }}>
          {['Yes', 'No'].map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '16px', color: '#1e293b' }}>
              <input type="radio" name="prior_pt" checked={form.prior_pt === (opt === 'Yes')} onChange={() => set('prior_pt', opt === 'Yes')} />
              {opt}
            </label>
          ))}
        </div>
      </Field>
      {form.prior_pt && (
        <Field label="Prior PT details">
          <textarea placeholder="When, what for, and was it helpful?" value={form.prior_pt_notes} onChange={e => set('prior_pt_notes', e.target.value)} style={{ ...iS, height: '72px', resize: 'vertical' }} />
        </Field>
      )}
      <Field label="Do you have any of the following conditions? (check all that apply)">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '6px' }}>
          {COMORBIDITIES.map(c => (
            <CheckboxItem
              key={c} label={c}
              checked={(form.comorbidities || []).includes(c)}
              onChange={() => toggleArrayItem('comorbidities', c)}
            />
          ))}
        </div>
      </Field>
      <Field label="Current Work Status">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '4px' }}>
          {WORK_STATUS_OPTIONS.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => set('work_status', opt)}
              style={{
                padding: '10px 12px', borderRadius: '8px', textAlign: 'left',
                border: form.work_status === opt ? `2px solid ${TEAL}` : '2px solid #e2e8f0',
                background: form.work_status === opt ? '#f0fdfa' : 'white',
                color: form.work_status === opt ? TEAL : '#64748b',
                fontSize: '14px', fontWeight: form.work_status === opt ? '700' : '400',
                cursor: 'pointer', fontFamily: FONT, transition: 'all 0.12s',
              }}
            >
              {form.work_status === opt ? ' ' : ''}{opt}
            </button>
          ))}
        </div>
      </Field>
    </StepCard>
  )
}

//  Step 2: Pain & Injury 

function Step2({ form, set, toggleArrayItem }) {
  return (
    <StepCard title="Pain & Injury Details">
      <SectionLabel>Pain Levels (0 = no pain, 10 = worst imaginable)</SectionLabel>
      <PainSlider label="Current pain level" value={form.pain_current} onChange={v => set('pain_current', v)} />
      <PainSlider label="Worst pain in the past week" value={form.pain_worst} onChange={v => set('pain_worst', v)} />
      <PainSlider label="Best (lowest) pain in the past week" value={form.pain_best} onChange={v => set('pain_best', v)} />

      <Field label="How would you describe your pain? (select all that apply)">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
          {PAIN_TYPES.map(t => {
            const selected = (form.pain_type || '').split(', ').filter(Boolean).includes(t)
            return (
              <TagToggle
                key={t} label={t} selected={selected}
                onToggle={() => {
                  const current = form.pain_type ? form.pain_type.split(', ').filter(Boolean) : []
                  const updated = current.includes(t) ? current.filter(x => x !== t) : [...current, t]
                  set('pain_type', updated.join(', '))
                }}
              />
            )
          })}
        </div>
      </Field>

      <Field label="Where is your pain located? (tap all that apply)">
        <BodyRegionSelector
          selected={form.pain_body_regions || []}
          toggle={(r) => toggleArrayItem('pain_body_regions', r)}
        />
      </Field>

      <SectionLabel>Injury Information</SectionLabel>
      <Field label="How did the injury occur?">
        <textarea
          placeholder="e.g. Twisted my knee playing soccer, or gradual onset after starting a new job..."
          value={form.mechanism_of_injury}
          onChange={e => set('mechanism_of_injury', e.target.value)}
          style={{ ...iS, height: '80px', resize: 'vertical' }}
        />
      </Field>
      <Field label="When did symptoms start?">
        <input type="date" value={form.symptom_onset} onChange={e => set('symptom_onset', e.target.value)} style={{ ...iS, maxWidth: '220px' }} />
      </Field>
      <TwoCol>
        <Field label="What makes it worse?">
          <textarea placeholder="e.g. Walking, stairs, prolonged sitting" value={form.aggravating_factors} onChange={e => set('aggravating_factors', e.target.value)} style={{ ...iS, height: '72px', resize: 'vertical' }} />
        </Field>
        <Field label="What makes it better?">
          <textarea placeholder="e.g. Rest, ice, elevation, heat" value={form.relieving_factors} onChange={e => set('relieving_factors', e.target.value)} style={{ ...iS, height: '72px', resize: 'vertical' }} />
        </Field>
      </TwoCol>
      <Field label="Functional limitations" hint="What activities can you no longer do, or do with difficulty?">
        <textarea
          placeholder="e.g. Can't climb stairs, difficulty getting in/out of car, can't sleep on my right side"
          value={form.functional_limitations}
          onChange={e => set('functional_limitations', e.target.value)}
          style={{ ...iS, height: '72px', resize: 'vertical' }}
        />
      </Field>
    </StepCard>
  )
}

//  Step 3: Lifestyle & Goals 

function Step3({ form, set }) {
  return (
    <StepCard title="Lifestyle & Goals">
      <TwoCol>
        <Field label="Occupation / Job Title">
          <input placeholder="e.g. Teacher, nurse, office worker" value={form.occupation} onChange={e => set('occupation', e.target.value)} style={iS} />
        </Field>
        <Field label="Sports or recreational activities">
          <input placeholder="e.g. Running, golf, tennis, yoga" value={form.sport_activities} onChange={e => set('sport_activities', e.target.value)} style={iS} />
        </Field>
      </TwoCol>
      <Field label="Overall activity level">
        <select value={form.activity_level} onChange={e => set('activity_level', e.target.value)} style={{ ...iS, color: form.activity_level ? '#1e293b' : '#94a3b8' }}>
          <option value="" disabled>Select activity level</option>
          <option>Sedentary (little to no exercise)</option>
          <option>Lightly active (1–2 days/week)</option>
          <option>Moderately active (3–4 days/week)</option>
          <option>Very active (5+ days/week)</option>
          <option>Athlete / competitive sport</option>
        </select>
      </Field>
      <Field label="What are your goals for physical therapy?" hint="Be as specific as possible — what does a successful recovery look like to you?">
        <textarea
          placeholder="e.g. Return to hiking without pain, play with my kids, get back to work full duty, run a 5K..."
          value={form.patient_goals}
          onChange={e => set('patient_goals', e.target.value)}
          style={{ ...iS, height: '100px', resize: 'vertical' }}
        />
      </Field>
    </StepCard>
  )
}

//  Step 4: Review & Consent 

function Step4({ form, set, patientName }) {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  return (
    <StepCard title="Review & Consent">
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px 20px', fontSize: '14px', color: '#475569', lineHeight: '1.75' }}>
        <p style={{ margin: '0 0 12px', fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>Consent to Evaluate and Treat</p>
        <p style={{ margin: '0 0 10px' }}>I voluntarily consent to physical therapy evaluation and treatment. I understand that treatment may include therapeutic exercise, manual therapy, physical modalities, and patient education. I have the right to refuse or discontinue treatment at any time. Treatment outcomes cannot be guaranteed.</p>

        <p style={{ margin: '12px 0 10px', fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>Financial Responsibility</p>
        <p style={{ margin: '0 0 10px' }}>I understand that I am financially responsible for all charges incurred for services rendered, including any portion not covered by my insurance. I authorize assignment of insurance benefits directly to ClearPath PT for services rendered.</p>

        <p style={{ margin: '12px 0 10px', fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>HIPAA Notice of Privacy Practices</p>
        <p style={{ margin: 0 }}>I acknowledge that I have been offered the Notice of Privacy Practices, which describes how my health information may be used and disclosed for treatment, payment, and healthcare operations. I understand my rights regarding my health information.</p>
      </div>

      <Field label="Sign by typing your initials" hint={`Patient: ${patientName || 'Patient'} · Date: ${today}`}>
        <input
          type="text"
          placeholder="e.g. JD"
          value={form.consent_initials}
          onChange={e => set('consent_initials', e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
          style={{ ...iS, fontSize: '24px', fontWeight: '800', textAlign: 'center', letterSpacing: '6px', maxWidth: '130px' }}
          maxLength={4}
          autoCapitalize="characters"
        />
      </Field>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginTop: '4px' }}>
        <input
          type="checkbox"
          checked={form.consent_signed}
          onChange={e => set('consent_signed', e.target.checked)}
          style={{ marginTop: '3px', width: '20px', height: '20px', flexShrink: 0, accentColor: TEAL }}
        />
        <span style={{ fontSize: '15px', color: '#1e293b', lineHeight: '1.6' }}>
          I have read and agree to the Consent to Treat, Financial Responsibility Agreement, and HIPAA Privacy Notice above. I confirm that all information provided in this intake form is accurate and complete to the best of my knowledge.
        </span>
      </label>

      {form.consent_signed && form.consent_initials.trim() && (
        <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '14px', color: '#15803d', fontWeight: '500' }}>
           Signed by {patientName} ({form.consent_initials}) on {today}
        </div>
      )}
    </StepCard>
  )
}

//  UI Components 

function PainSlider({ label, value, onChange }) {
  const color = value <= 2 ? '#22c55e' : value <= 4 ? '#84cc16' : value <= 6 ? '#f59e0b' : value <= 8 ? '#ef4444' : '#b91c1c'
  const descriptions = ['No pain', 'Very mild', 'Mild', 'Noticeable', 'Moderate', 'Moderate-severe', 'Severe', 'Very severe', 'Intense', 'Excruciating', 'Unbearable']
  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <label style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '26px', fontWeight: '800', color, fontFamily: FONT, lineHeight: 1, minWidth: '28px', textAlign: 'right' }}>{value}</span>
          <span style={{ fontSize: '13px', color: '#64748b', maxWidth: '90px', lineHeight: '1.2', textAlign: 'left' }}>{descriptions[value]}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'space-between' }}>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => {
          const btnColor = n <= 2 ? '#22c55e' : n <= 4 ? '#84cc16' : n <= 6 ? '#f59e0b' : n <= 8 ? '#ef4444' : '#b91c1c'
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                flex: 1, height: '38px', border: value === n ? `2px solid ${btnColor}` : '2px solid #e2e8f0',
                borderRadius: '7px', cursor: 'pointer',
                background: value === n ? btnColor : 'white',
                color: value === n ? 'white' : '#94a3b8',
                fontSize: '14px', fontWeight: value === n ? '800' : '500',
                padding: 0, fontFamily: FONT,
                transition: 'all 0.12s',
              }}
            >
              {n}
            </button>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>No pain</span>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>Worst possible</span>
      </div>
    </div>
  )
}

function BodyRegionSelector({ selected, toggle }) {
  return (
    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {BODY_REGION_GROUPS.map(group => (
        <div key={group.label}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '7px' }}>
            {group.label}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${group.cols}, 1fr)`, gap: '7px' }}>
            {group.regions.map(region => (
              <button
                key={region}
                type="button"
                onClick={() => toggle(region)}
                style={{
                  padding: '9px 12px', borderRadius: '8px', textAlign: 'left',
                  border: selected.includes(region) ? `2px solid ${TEAL}` : '2px solid #e2e8f0',
                  background: selected.includes(region) ? '#f0fdfa' : 'white',
                  color: selected.includes(region) ? TEAL : '#64748b',
                  fontSize: '13px', fontWeight: selected.includes(region) ? '700' : '400',
                  cursor: 'pointer', fontFamily: FONT, transition: 'all 0.12s', lineHeight: '1.3',
                }}
              >
                {selected.includes(region) ? ' ' : ''}{region}
              </button>
            ))}
          </div>
        </div>
      ))}
      {selected.length > 0 && (
        <p style={{ fontSize: '13px', color: TEAL, margin: '0', fontWeight: '600' }}>
          Selected: {selected.join(' · ')}
        </p>
      )}
    </div>
  )
}

function TagToggle({ label, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        padding: '8px 14px', borderRadius: '99px',
        border: selected ? `2px solid ${TEAL}` : '2px solid #e2e8f0',
        background: selected ? '#f0fdfa' : 'white',
        color: selected ? TEAL : '#64748b',
        fontSize: '14px', fontWeight: selected ? '600' : '400',
        cursor: 'pointer', fontFamily: FONT, transition: 'all 0.12s',
      }}
    >
      {selected ? ' ' : ''}{label}
    </button>
  )
}

function CheckboxItem({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '15px', color: '#1e293b', lineHeight: '1.4' }}>
      <input
        type="checkbox" checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ width: '17px', height: '17px', flexShrink: 0, marginTop: '2px', accentColor: TEAL }}
      />
      {label}
    </label>
  )
}

function SectionLabel({ children, style }) {
  return (
    <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.09em', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9', ...style }}>
      {children}
    </div>
  )
}

function TwoCol({ children }) {
  return <div className="intake-twocol" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>{children}</div>
}

function ErrBox({ children, style }) {
  return (
    <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', ...style }}>
      {children}
    </div>
  )
}

function Screen({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'transparent', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', fontFamily: FONT }}>
      <style>{`
        .intake-twocol { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .intake-card { padding: 36px 32px; }
        @media (max-width: 520px) {
          .intake-twocol { grid-template-columns: 1fr !important; }
          .intake-card { padding: 24px 18px !important; border-radius: 0 !important; }
        }
      `}</style>
      <div className="intake-card" style={{ width: '100%', maxWidth: '640px', background: 'white', borderRadius: '14px', padding: '36px 32px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' }}>
        {children}
      </div>
    </div>
  )
}

function StepCard({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>{children}</div>
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: hint ? '2px' : '6px' }}>{label}</label>
      {hint && <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 6px' }}>{hint}</p>}
      {children}
    </div>
  )
}

const iS = {
  width: '100%', padding: '10px 12px', fontSize: '16px',
  border: '1.5px solid #e2e8f0', borderRadius: '8px',
  boxSizing: 'border-box', color: '#1e293b', background: 'white',
  fontFamily: FONT, outline: 'none',
}

const btnPrimary = {
  padding: '11px 28px', background: TEAL, color: 'white',
  border: 'none', borderRadius: '8px', cursor: 'pointer',
  fontSize: '15px', fontWeight: '600', fontFamily: FONT,
}
